import React, { useState, useEffect, Component } from "react";
import { useNavigate } from "react-router-dom";
import { Loading } from "../components/Loading"; // Import the updated Loading component
import "./Payments.css";
import { Button } from "../components/Utilities";
import { apiService } from "../../services/apiService";
import { useCarContext } from "../CarContext";
import "../MyCars/Inspection.css";
import CardLayout from "../components/CardLayout";
import { UploadPaymentModal } from "../components/ModelOverlay/PaymentModal";
import { formatOwnership } from "../DealerHome";

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h3>Something went wrong</h3>
          <p>{this.state.error?.message || "An unexpected error occurred."}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function Payments() {
  const navigate = useNavigate();
  const { setPaymentsCount } = useCarContext();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUploadPaymentModalOpen, setIsUploadPaymentModalOpen] = useState(false);
  const [selectedCarId, setSelectedCarId] = useState(null);

  useEffect(() => {
    const fetchPaymentCars = async () => {
      try {
        setLoading(true);
        const response = await apiService.getDealerPaymentCars();
        console.log("getDealerPaymentCars response:", JSON.stringify(response.data, null, 2));
        
        // Debug: Check if awardedToDealerAmount is in the response
        if (response.data && response.data.yardGroups) {
          response.data.yardGroups.forEach((group, groupIndex) => {
            if (group.cars) {
              group.cars.forEach((car, carIndex) => {
                console.log(`🔍 Car ${carIndex} in group ${groupIndex}:`, {
                  id: car.id,
                  awardedToDealerAmount: car.awardedToDealerAmount,
                  awardedAmount: car.awardedAmount,
                  state: car.state,
                  allKeys: Object.keys(car)
                });
                
                // Check if the field exists in the raw car object
                console.log(`🔍 Raw car object for ${car.id}:`, car);
                console.log(`🔍 Checking for awardedToDealerAmount in car:`, 'awardedToDealerAmount' in car);
                console.log(`🔍 Checking for awardedAmount in car:`, 'awardedAmount' in car);
              });
            }
          });
        }
const handleViewPaymentProof = (carId) => {
  console.log("Opening View Payment Modal for carId:", carId);
  setSelectedCarId(carId);
  setIsUploadPaymentModalOpen(true);
};
        if (!response.data || !response.data.yardGroups) {
          console.warn("No yardGroups found in response:", response.data);
          setError("No cars available for payment.");
          setCars([]);
          setPaymentsCount(0);
          setLoading(false);
          return;
        }

        const validCars = response.data.yardGroups
          .flatMap((group) => group.cars || [])
          .filter((car) => ["BID_AWARDED", "PAYMENT_RECORDED", "PAYMENT_REVIEWED", "PAYMENT_REJECTED"].includes(car.state))
          .map((car, index) => {
            const originalCarId = car.id || `car-${Math.random().toString(36).substr(2, 9)}`;
            return {
              id: `${originalCarId}-Payment-${index}`,
              originalCarId,
              flow_entity_id: car.flow_entity_id,
              brand: car.make || "Unknown",
              model: car.model || "Unknown",
              rto: car.regno || "N/A",
              fuel: car.fueltype || "Unknown",
              transmission: car.transmission || "N/A",
              year: Number(car.year) || 0,
              // awardedToDealerAmount:car.awardedToDealerAmount || 0,
              km: Number(car.kmsrun) || 0,
              price: Number(car.latestBidValue) || (Number(car.auctionWinAmount) || 0) + (Number(car.auctionDepositAmount) || 0),
              location: car.yard || "Unknown Location",
                   rc:
                          car.rcstatus?.toUpperCase() === "YES" ||
                          car.rcstatus?.toUpperCase() === "Y" ||
                          car.rcstatus?.toUpperCase() === "AVAILABLE",
                startDate: car["auctionAwardedAt"],
          endDate: car["next_deadline"],
          paymentInfo: car.paymentInfo || [],
                    owner: formatOwnership(car.ownership),
              auctionId: car.auctionId || "Unknown",
              state: car.state,
              awardedToDealerAmount: Number(car.awardedToDealerAmount) || 0,
              awardedAmount: Number(car.awardedAmount) || 0,
  evaluationImages: car.evaluationImages || [],
  car_image_urls: car.car_image_urls || [],
            };
          });

        console.log("Processed validCars:", validCars);
        if (validCars.length === 0) {
          setError("No cars pending payment.");
          setPaymentsCount(0);
        } else {
          setPaymentsCount(validCars.length);
        }
        setCars(validCars);
        setError(null);
      } catch (err) {
        console.error("Error fetching dealer payment cars:", {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
        });
        let errorMessage = "Failed to load cars. Please try again.";
        if (err.response?.status === 401) {
          errorMessage = "Unauthorized. Redirecting to login.";
          navigate("/login");
        } else if (err.response?.status === 400) {
          errorMessage = `Invalid request: ${err.response.data?.error || "Please check your inputs."}`;
        } else if (err.response?.status === 500) {
          errorMessage = `Server error: ${err.response.data?.error || "Please try again later."}`;
        } else if (err.code === "ECONNABORTED") {
          errorMessage = "Request timed out. Please check your network.";
        }
        setError(errorMessage);
        setPaymentsCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentCars();
  }, [navigate, setPaymentsCount]);

  const totalPaymentPending = cars.reduce((sum, car) => sum + (car.price || 0), 0);
  const displayCars = [...cars, { id: "total-payment", isTotalPayment: true }];

  const handleUploadPaymentProof = (carId) => {
    console.log("Opening UploadPaymentModal for carId:", carId);
    setSelectedCarId(carId);
    setIsUploadPaymentModalOpen(true);
  };

  const handleRemoveCar = (carId) => {
    console.log(`Removing car with ID: ${carId}`);
    setCars((prev) => {
      const newCars = prev.filter((car) => car.id !== carId);
      console.log("Updated cars after removal, count:", newCars.length, newCars);
      setPaymentsCount(newCars.length);
      return newCars;
    });
    if (selectedCarId === carId) {
      setIsUploadPaymentModalOpen(false);
      setSelectedCarId(null);
    }
  };

  const handlePaymentSubmitted = (carId) => {
    console.log(`Payment submitted for car ID: ${carId}`);
    setCars((prev) =>
      prev.map((car) =>
        car.id === carId ? { ...car, state: "PAYMENT_RECORDED" } : car
      )
    );
    setIsUploadPaymentModalOpen(false); // Close the modal without reload
    window.location.reload()
  };

  const handleCloseUploadPaymentModal = () => {
    setIsUploadPaymentModalOpen(false);
    setSelectedCarId(null);
    window.location.reload();
  };

  const handleClickToCall = () => {
    console.log("Click to Call initiated");
  };

  const handleSeeMoreCars = () => {
    console.log("See More Cars clicked");
    navigate("/DealerHome");
  };

  const handleViewPaymentProof = (carId) => {
  console.log("Opening View Payment Modal for carId:", carId);
  setSelectedCarId(carId);
  setIsUploadPaymentModalOpen(true);
};

  return (
    <ErrorBoundary>
      <div className="payments">
   
          <div className="content-wrapper">
            <div className="main-content">
              <div className="car-grid-container">
                {loading && <Loading />}
                {error && (
                  <div className="no-results">
                    <h3>Error</h3>
                    <p>{error}</p>
                    <Button className="see-more" variant="primary" onClick={handleSeeMoreCars}>
                       Back to Home
                    </Button>
                  </div>
                )}
                {!loading && !error && displayCars.length === 1 && displayCars[0].isTotalPayment && (
                  <div className="no-results">
                    <h3>No cars found</h3>
                    <p>You don’t have any winning bids now.</p>
                    <Button className="see-more" variant="primary" onClick={handleSeeMoreCars}>
                      Back to Home
                    </Button>
                  </div>
                )}
                {!loading && !error && displayCars.length > 1 && (
                  <div className="car-grid">
                    {displayCars.map((car) =>
                      car.isTotalPayment ? (
                        <></>
                      ) : (
                        <CardLayout
                          key={car.id}
                          car={car}
                          btn2={() => navigate(`/car-details/${car.originalCarId}`)}
                         // Inside CardLayout in Payments.js
btn1={() => 
  (car.state === "BID_AWARDED" || car.state === "PAYMENT_REJECTED")
    ? handleUploadPaymentProof(car.id)
    : handleViewPaymentProof(car.id)  // New handler for view
}
btn1_label={
  (car.state === "BID_AWARDED" || car.state === "PAYMENT_REJECTED")
    ? (car.state === "PAYMENT_REJECTED" ? "Re-upload Payment Proof" : "Upload Payment Proof")
    : "View Payment Proof"  // Show "View" instead of "Pending"
}

                         
          
                          showBidInfo={false}
                        />
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
   
        <UploadPaymentModal
          isOpen={isUploadPaymentModalOpen}
          onClose={handleCloseUploadPaymentModal}
          car={cars.find((car) => car.id === selectedCarId) || {}}
          onPaymentSubmitted={handlePaymentSubmitted}
          viewMode={
    selectedCarId
      ? ["PAYMENT_RECORDED", "PAYMENT_REVIEWED"].includes(
          cars.find((c) => c.id === selectedCarId)?.state
        )
      : false
  }
        />
      </div>
    </ErrorBoundary>
  );
}